// src/pages/JobDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiRequest } from "../api";
import { getCustomerSuccessState } from "../components/customer_success/customerSuccessEngine";

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return String(dt);
  }
}

const STATUS_OPTIONS = [
  { value: "PROSPECT", label: "Prospect" },
  { value: "ESTIMATE_SENT", label: "Estimate Sent" },
  { value: "ESTIMATE_APPROVED", label: "Estimate Approved" },
  { value: "JOB_IN_PROGRESS", label: "Job In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "INVOICED", label: "Invoiced" },
  { value: "PAID", label: "Paid" },
];

function statusLabel(value) {
  const found = STATUS_OPTIONS.find((s) => s.value === value);
  return found ? found.label : value || "—";
}

function statusPillStyle(value) {
  switch (value) {
    case "PROSPECT":
      return { background: "rgba(255,92,92,0.16)", color: "#ff7b7b" };
    case "ESTIMATE_SENT":
      return { background: "rgba(245,158,11,0.16)", color: "#fbbf24" };
    case "ESTIMATE_APPROVED":
      return { background: "rgba(139,92,246,0.16)", color: "#c4b5fd" };
    case "JOB_IN_PROGRESS":
      return { background: "rgba(59,130,246,0.16)", color: "#93c5fd" };
    case "COMPLETED":
      return { background: "rgba(34,197,94,0.16)", color: "#86efac" };
    case "INVOICED":
      return { background: "rgba(14,165,233,0.16)", color: "#67e8f9" };
    case "PAID":
      return { background: "rgba(22,163,74,0.16)", color: "#86efac" };
    default:
      return { background: "rgba(255,255,255,0.10)", color: "#dbe7f3" };
  }
}

function money(v) {
  const n = Number(v || 0);
  return `$${n.toFixed(2)}`;
}

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [servers, setServers] = useState([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [savingAssignee, setSavingAssignee] = useState(false);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const [projectInvoices, setProjectInvoices] = useState([]);
  const [loadingInvoices] = useState(false);
  const [dismissedCustomerSuccess, setDismissedCustomerSuccess] = useState("");

  function dismissCustomerSuccess(key) {
    setDismissedCustomerSuccess(key);
  }

  function reopenCustomerSuccess() {
    setDismissedCustomerSuccess("");
  }

  const [jobCosts, setJobCosts] = useState([]);
  const [savingCost, setSavingCost] = useState(false);
  const [costForm, setCostForm] = useState({
    description: "",
    vendor_name: "",
    cost_category: "Materials",
    quantity: "1",
    unit_cost: "",
    cost_status: "actual",
    receipt_file_url: "",
    bill_file_url: "",
    notes: "",
  });

  const [costCategoryFilter, setCostCategoryFilter] = useState("all");
  const [costVendorFilter, setCostVendorFilter] = useState("all");

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const [data, srv, inv, costs] = await Promise.all([
        apiRequest(`/jobs/${jobId}/detail`),
        apiRequest(`/servers/`),
        apiRequest(`/jobs/${jobId}/invoices`),
        apiRequest(`/jobs/${jobId}/costs`),
      ]);

      setDetail(data);
      setServers(Array.isArray(srv) ? srv : []);
      setProjectInvoices(Array.isArray(inv) ? inv : []);
      setJobCosts(Array.isArray(costs) ? costs : []);
      setAssigneeId(
        data?.job?.assigned_server_id ? String(data.job.assigned_server_id) : ""
      );
    } catch (e) {
      setErr(e.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  async function changeAssignee(newVal) {
    setSavingAssignee(true);
    setErr("");
    try {
      const payload = { server_id: newVal ? Number(newVal) : null };
      await apiRequest(`/jobs/${jobId}/assignee`, {
        method: "PATCH",
        body: payload,
      });
      setAssigneeId(newVal);
      await load();
    } catch (e) {
      setErr(e.message || "Assign failed.");
    } finally {
      setSavingAssignee(false);
    }
  }

  async function changeStatus(newStatus) {
    setErr("");
    try {
      await apiRequest(`/jobs/${jobId}`, {
        method: "PUT",
        body: {
          ...detail.job,
          status: newStatus,
        },
      });
      await load();
    } catch (e) {
      setErr(e.message || "Failed to update status.");
    }
  }

  async function addNote() {
    if (!noteText.trim()) return;
    setSavingNote(true);
    setErr("");
    try {
      const payload = {
        parent_type: "job",
        parent_id: Number(jobId),
        content: noteText,
      };
      await apiRequest(`/notes/`, {
        method: "POST",
        body: payload,
      });
      setNoteText("");
      await load();
    } catch (e) {
      setErr(e.message || "Error creating note.");
    } finally {
      setSavingNote(false);
    }
  }

  async function addJobCost() {
    if (!costForm.description.trim()) return;
    setSavingCost(true);
    setErr("");
    try {
      await apiRequest(`/jobs/${jobId}/costs`, {
        method: "POST",
        body: {
          description: costForm.description,
          vendor_name: costForm.vendor_name || null,
          cost_category: costForm.cost_category || null,
          quantity: Number(costForm.quantity || 1),
          unit_cost: Number(costForm.unit_cost || 0),
          cost_status: costForm.cost_status || "actual",
          receipt_file_url: costForm.receipt_file_url || null,
          bill_file_url: costForm.bill_file_url || null,
          notes: costForm.notes || null,
        },
      });

      setCostForm({
        description: "",
        vendor_name: "",
        cost_category: "Materials",
        quantity: "1",
        unit_cost: "",
        cost_status: "actual",
        receipt_file_url: "",
        bill_file_url: "",
        notes: "",
      });

      await load();
    } catch (e) {
      setErr(e.message || "Error creating job cost.");
    } finally {
      setSavingCost(false);
    }
  }

  async function deleteJobCost(costId) {
    setErr("");
    try {
      await apiRequest(`/jobs/costs/${costId}`, {
        method: "DELETE",
      });
      await load();
    } catch (e) {
      setErr(e.message || "Error deleting job cost.");
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 24, color: "#ffffff" }}>
        <p>Loading…</p>
      </div>
    );
  }

  if (err) {
    return (
      <div style={{ padding: 24, color: "#ffffff" }}>
        <div
          style={{
            background: "rgba(255,138,138,0.10)",
            border: "1px solid rgba(255,138,138,0.30)",
            color: "#ff8a8a",
            padding: "10px 12px",
            borderRadius: 10,
            marginBottom: 14,
          }}
        >
          {err}
        </div>
        <p>
          <button onClick={load}>Retry</button>{" "}
          <Link to="/">Back to Clients & Jobs</Link>
        </p>
      </div>
    );
  }

  if (!detail) return null;

  const { job, client, notes = [] } = detail;

  const filteredJobCosts = jobCosts.filter((c) => {
    const categoryMatch =
      costCategoryFilter === "all" || c.cost_category === costCategoryFilter;
    const vendorMatch =
      costVendorFilter === "all" || (c.vendor_name || "—") === costVendorFilter;
    return categoryMatch && vendorMatch;
  });

  const costCategories = Array.from(
    new Set(jobCosts.map((c) => c.cost_category).filter(Boolean))
  ).sort();

  const costVendors = Array.from(
    new Set(jobCosts.map((c) => c.vendor_name || "—"))
  ).sort();

  const totalJobCosts = jobCosts.reduce(
    (sum, c) => sum + Number(c.total_cost || 0),
    0
  );

  const filteredJobCostsTotal = filteredJobCosts.reduce(
    (sum, c) => sum + Number(c.total_cost || 0),
    0
  );


  const customerSuccess = getCustomerSuccessState({
    invoices: projectInvoices,
    context: "job-detail",
  });

  function handleCustomerSuccessAction(action) {
    if (action === "create-estimate") {
      navigate(`/estimates/new?job_id=${jobId}`);
      return;
    }

    if (action === "create-invoice") {
      navigate(`/invoices/new?job_id=${jobId}`);
    }
  }

  const jobRevenue = projectInvoices.reduce(
    (sum, inv) => sum + Number(inv.total || 0),
    0
  );

  const estimatedJobCosts = jobCosts
    .filter((c) => c.cost_status === "estimated")
    .reduce((sum, c) => sum + Number(c.total_cost || 0), 0);

  const actualJobCosts = jobCosts
    .filter((c) => c.cost_status !== "estimated")
    .reduce((sum, c) => sum + Number(c.total_cost || 0), 0);

  const costVariance = actualJobCosts - estimatedJobCosts;

  const categoryTotals = {};

  jobCosts.forEach((c) => {
    const category = c.cost_category || "Other";
    categoryTotals[category] =
      (categoryTotals[category] || 0) + Number(c.total_cost || 0);
  });

  const sortedCategoryTotals = Object.entries(categoryTotals)
    .sort((a, b) => b[1] - a[1]);

  const jobProfit = jobRevenue - totalJobCosts;
  const jobMargin = jobRevenue > 0 ? (jobProfit / jobRevenue) * 100 : 0;

  const page = {
    padding: 24,
    maxWidth: 1100,
    margin: "0 auto",
    color: "#ffffff",
  };

  const card = {
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: 20,
    background: "#10263a",
    marginBottom: 18,
  };

  const subCard = {
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 14,
    padding: 16,
    background: "#10263a",
    marginBottom: 0,
  };

  const sectionLabel = {
    fontSize: 11,
    textTransform: "uppercase",
    color: "#9fb0c3",
    marginBottom: 6,
    letterSpacing: 0.4,
  };

  const input = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "#0f2235",
    color: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    transition: "all 0.2s ease",
  };

  const buttonPrimary = {
    background: "#f5c542",
    color: "#0b1622",
    border: "none",
    borderRadius: 10,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const buttonSecondary = {
    background: "#1b2e44",
    color: "#ffffff",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 10,
    padding: "10px 14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const linkStyle = {
    color: "#00c2ff",
    textDecoration: "none",
    fontWeight: 600,
  };

  const tableTh = {
    textAlign: "left",
    padding: "10px 8px",
    borderBottom: "1px solid rgba(255,255,255,0.10)",
    fontWeight: 700,
    fontSize: 13,
    color: "#dbe7f3",
    whiteSpace: "nowrap",
  };

  const tableTd = {
    padding: "10px 8px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    fontSize: 13,
    color: "#ffffff",
    verticalAlign: "top",
  };

  const statusPill = {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    marginLeft: 8,
    ...statusPillStyle(job.status),
  };

  const focusHandlers = {
    onFocus: (e) => {
      e.target.style.border = "1px solid #f5c542";
      e.target.style.boxShadow = "0 0 0 3px rgba(245,197,66,0.12)";
    },
    onBlur: (e) => {
      e.target.style.border = "1px solid rgba(255,255,255,0.10)";
      e.target.style.boxShadow = "none";
    },
  };

  const primaryHoverHandlers = {
    onMouseEnter: (e) => {
      e.target.style.background = "#e0b437";
      e.target.style.transform = "translateY(-1px)";
    },
    onMouseLeave: (e) => {
      e.target.style.background = "#f5c542";
      e.target.style.transform = "translateY(0)";
    },
  };

  const secondaryHoverHandlers = {
    onMouseEnter: (e) => {
      e.target.style.background = "#24374d";
      e.target.style.transform = "translateY(-1px)";
    },
    onMouseLeave: (e) => {
      e.target.style.background = "#1b2e44";
      e.target.style.transform = "translateY(0)";
    },
  };

  return (
    <div style={page}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={buttonSecondary}
          {...secondaryHoverHandlers}
        >
          ← Back
        </button>
        <Link to="/" style={linkStyle}>
          Home
        </Link>
      </div>

      <div style={card}>
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h2 style={{ margin: 0, color: "#ffffff", fontSize: 18 }}>
              JOB ID #{job.id}
            </h2>

            <div style={{ color: "#9fb0c3", fontSize: 14, marginTop: 6 }}>
              Status:
              <span style={statusPill}>{statusLabel(job.status)}</span>
              {job.due_date ? <> · Due {fmt(job.due_date)}</> : null}
            </div>

            <div style={{ color: "#9fb0c3", fontSize: 14, marginTop: 6 }}>
              Project Type: {job.court_name || "—"} · Project #{" "}
              {job.case_number || "—"}
            </div>

            <div style={{ color: "#9fb0c3", fontSize: 12, marginTop: 6 }}>
              Created: {fmt(job.date_created)}
            </div>

            <div style={{ marginTop: 14 }}>
              <div style={sectionLabel}>Assigned crew / staff</div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 4,
                }}
              >
                <select
                  value={assigneeId}
                  onChange={(e) => changeAssignee(e.target.value)}
                  disabled={savingAssignee}
                  style={{ ...input, minWidth: 300, maxWidth: 300 }}
                  {...focusHandlers}
                >
                  <option value="">— Unassigned —</option>
                  {servers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name || `Staff #${s.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 12,
            marginTop: 18,
          }}
        >
          <div style={subCard}>
            <div style={sectionLabel}>Client</div>
            <div style={{ fontWeight: 700, color: "#ffffff", marginBottom: 4 }}>
              {client?.company_name || client?.contact_name || "—"}
            </div>
            <div style={{ color: "#ffffff" }}>{client?.email || "—"}</div>
            <div style={{ color: "#ffffff" }}>{client?.phone || "—"}</div>
            <div style={{ color: "#ffffff" }}>{client?.address || "—"}</div>
          </div>

          <div style={subCard}>
            <div style={sectionLabel}>Project</div>
            <div style={{ fontWeight: 700, color: "#ffffff", marginBottom: 4 }}>
              {job.defendant_name || "—"}
            </div>
            <div style={{ color: "#dbe7f3" }}>Job Site Address:</div>
            <div style={{ color: "#ffffff" }}>{job.service_address || "—"}</div>
          </div>

          <div style={subCard}>
            <div style={sectionLabel}>Internal Notes</div>
            <div style={{ color: "#ffffff" }}>
              {job.special_instructions || "—"}
            </div>
          </div>
        </div>
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0, marginBottom: 14, color: "#ffffff" }}>
          JOB STATUS
        </h3>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <select
            value={job.status || ""}
            onChange={(e) => changeStatus(e.target.value)}
            style={{ ...input, minWidth: 260 }}
            {...focusHandlers}
          >
            <option value="">Select Status</option>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0, marginBottom: 14, color: "#ffffff" }}>
          JOB NOTES
        </h3>

        {notes.length === 0 ? (
          <p style={{ color: "#9fb0c3" }}>No notes yet.</p>
        ) : (
          <div style={{ marginBottom: 12 }}>
            {notes.map((n) => (
              <div
                key={n.id}
                style={{
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                  padding: "8px 0",
                  fontSize: 14,
                }}
              >
                <div style={{ color: "#ffffff" }}>{n.content}</div>
                <div style={{ color: "#9fb0c3", fontSize: 12 }}>
                  {fmt(n.timestamp)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a note..."
            rows={3}
            style={{
              ...input,
              resize: "vertical",
              minHeight: 80,
              flex: 1,
            }}
            {...focusHandlers}
          />
          <button
            onClick={addNote}
            disabled={savingNote}
            style={buttonPrimary}
            {...primaryHoverHandlers}
          >
            {savingNote ? "Saving..." : "Add Note"}
          </button>
        </div>

        <div style={{ ...subCard, marginTop: 18 }}>
          <h3 style={{ marginTop: 0, marginBottom: 14, color: "#ffffff" }}>
            JOB REVENUE WORKFLOW
          </h3>

          {customerSuccess && (
            dismissedCustomerSuccess === customerSuccess.key ? (
              <button
                type="button"
                onClick={reopenCustomerSuccess}
                style={{
                  marginBottom: 16,
                  padding: 0,
                  border: "none",
                  background: "transparent",
                  color: "#00c2ff",
                  fontWeight: 800,
                  cursor: "pointer",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                  fontSize: 14,
                }}
              >
                {customerSuccess.helpLabel}
              </button>
            ) : (
            <div
              style={{
                marginBottom: 16,
                padding: 16,
                borderRadius: 14,
                border: "1px solid rgba(245,197,66,0.28)",
                background:
                  "linear-gradient(135deg, rgba(245,197,66,0.10), rgba(16,38,58,0.96))",
              }}
            >
              <button
                type="button"
                onClick={() => dismissCustomerSuccess(customerSuccess.key)}
                aria-label="Close customer success"
                style={{
                  float: "right",
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.16)",
                  background: "rgba(15,34,53,0.85)",
                  color: "#dbeafe",
                  cursor: "pointer",
                  fontWeight: 900,
                }}
              >
                ×
              </button>

              <div
                style={{
                  color: "#f5c542",
                  fontSize: 12,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 6,
                }}
              >
                Customer Success
              </div>
              <div
                style={{
                  color: "#ffffff",
                  fontSize: 16,
                  fontWeight: 800,
                  marginBottom: 6,
                }}
              >
                {customerSuccess.title}
              </div>
              <div style={{ color: "#c9d8e8", fontSize: 14, lineHeight: 1.55 }}>
                {customerSuccess.body}
              </div>
            </div>
            )
          )}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={() => navigate(`/estimates/new?job_id=${jobId}`)}
              style={buttonPrimary}
              {...primaryHoverHandlers}
            >
              + Create Estimate
            </button>

            <button
              onClick={() => navigate(`/invoices/new?job_id=${jobId}`)}
              style={buttonPrimary}
              {...primaryHoverHandlers}
            >
              + Skip to Invoice
            </button>
          </div>

          <div style={{ marginTop: 14 }}>
            {loadingInvoices ? (
              <p style={{ color: "#9fb0c3" }}>Loading invoices…</p>
            ) : projectInvoices.length === 0 ? (
              <p style={{ color: "#9fb0c3" }}>
                No invoices linked to this project yet.
              </p>
            ) : (
              <table
                style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}
              >
                <thead>
                  <tr>
                    <th style={tableTh}>Invoice #</th>
                    <th style={tableTh}>Status</th>
                    <th style={tableTh}>Issue Date</th>
                    <th style={tableTh}>Due Date</th>
                    <th style={tableTh}>Total</th>
                    <th style={tableTh}>Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {projectInvoices.map((inv) => (
                    <tr key={inv.id}>
                      <td style={tableTd}>
                        {inv.invoice_number || `Invoice #${inv.id}`}
                      </td>
                      <td style={tableTd}>{inv.status || "—"}</td>
                      <td style={tableTd}>{inv.issue_date || "—"}</td>
                      <td style={tableTd}>{inv.due_date || "—"}</td>
                      <td style={tableTd}>{money(inv.total)}</td>
                      <td style={tableTd}>{money(inv.amount_paid)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div style={{ ...subCard, marginTop: 18 }}>
          <h3 style={{ marginTop: 0, marginBottom: 8, color: "#ffffff" }}>
            JOB COSTS
          </h3>

          <div style={{ color: "#9fb0c3", fontSize: 14, marginBottom: 14 }}>
            Track materials, labor, vendors, receipts, bills, and other expenses tied directly to this job.
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.5fr 1fr 1fr 0.6fr 0.8fr 0.8fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <input
              value={costForm.description}
              onChange={(e) =>
                setCostForm({ ...costForm, description: e.target.value })
              }
              placeholder="Description"
              style={input}
              {...focusHandlers}
            />

            <input
              value={costForm.vendor_name}
              onChange={(e) =>
                setCostForm({ ...costForm, vendor_name: e.target.value })
              }
              placeholder="Vendor"
              style={input}
              {...focusHandlers}
            />

            <select
              value={costForm.cost_category}
              onChange={(e) =>
                setCostForm({ ...costForm, cost_category: e.target.value })
              }
              style={input}
              {...focusHandlers}
            >
              <option value="Materials">Materials</option>
              <option value="Labor">Labor</option>
              <option value="Subcontractors">Subcontractors</option>
              <option value="Equipment">Equipment</option>
              <option value="Mileage">Mileage</option>
              <option value="Fuel">Fuel</option>
              <option value="Permits">Permits</option>
              <option value="Dump Fees">Dump Fees</option>
              <option value="Tools">Tools</option>
              <option value="Rental Equipment">Rental Equipment</option>
              <option value="Office / Admin">Office / Admin</option>
              <option value="Other">Other</option>
            </select>

            <input
              value={costForm.quantity}
              onChange={(e) =>
                setCostForm({ ...costForm, quantity: e.target.value })
              }
              placeholder="Qty"
              type="number"
              step="0.01"
              style={input}
              {...focusHandlers}
            />

            <input
              value={costForm.unit_cost}
              onChange={(e) =>
                setCostForm({ ...costForm, unit_cost: e.target.value })
              }
              placeholder="Unit Cost"
              type="number"
              step="0.01"
              style={input}
              {...focusHandlers}
            />

            <select
              value={costForm.cost_status}
              onChange={(e) =>
                setCostForm({ ...costForm, cost_status: e.target.value })
              }
              style={input}
              {...focusHandlers}
            >
              <option value="actual">Actual</option>
              <option value="estimated">Estimated</option>
            </select>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <input
              value={costForm.receipt_file_url}
              onChange={(e) =>
                setCostForm({ ...costForm, receipt_file_url: e.target.value })
              }
              placeholder="Receipt URL"
              style={input}
              {...focusHandlers}
            />

            <input
              value={costForm.bill_file_url}
              onChange={(e) =>
                setCostForm({ ...costForm, bill_file_url: e.target.value })
              }
              placeholder="Bill URL"
              style={input}
              {...focusHandlers}
            />
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <input
              value={costForm.notes}
              onChange={(e) =>
                setCostForm({ ...costForm, notes: e.target.value })
              }
              placeholder="Notes"
              style={{ ...input, flex: 1 }}
              {...focusHandlers}
            />

            <button
              onClick={addJobCost}
              disabled={savingCost}
              style={buttonPrimary}
              {...primaryHoverHandlers}
            >
              {savingCost ? "Saving..." : "+ Add Job Cost"}
            </button>
          </div>

          <div
            style={{
              border: "1px solid rgba(245,197,66,0.20)",
              borderRadius: 14,
              padding: 14,
              background: "rgba(245,197,66,0.08)",
              marginBottom: 14,
            }}
          >
            <div style={sectionLabel}>Job Profit Summary</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              <div>
                <div style={{ color: "#9fb0c3", fontSize: 12 }}>Revenue</div>
                <div style={{ color: "#ffffff", fontSize: 20, fontWeight: 900 }}>
                  {money(jobRevenue)}
                </div>
              </div>
              <div>
                <div style={{ color: "#9fb0c3", fontSize: 12 }}>Costs</div>
                <div style={{ color: "#f5c542", fontSize: 20, fontWeight: 900 }}>
                  {money(totalJobCosts)}
                </div>
              </div>
              <div>
                <div style={{ color: "#9fb0c3", fontSize: 12 }}>Profit</div>
                <div style={{ color: "#86efac", fontSize: 20, fontWeight: 900 }}>
                  {money(jobProfit)}
                </div>
              </div>
              <div>
                <div style={{ color: "#9fb0c3", fontSize: 12 }}>Margin</div>
                <div style={{ color: "#67e8f9", fontSize: 20, fontWeight: 900 }}>
                  {jobRevenue > 0 ? `${jobMargin.toFixed(2)}%` : "—"}
                </div>
              </div>
            </div>
            <div style={{ color: "#9fb0c3", fontSize: 13, marginTop: 8 }}>
              Revenue is based on linked invoice totals. Costs are based on job costs entered below.
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 12,
                marginTop: 14,
                paddingTop: 12,
                borderTop: "1px solid rgba(255,255,255,0.10)",
              }}
            >
              <div>
                <div style={{ color: "#9fb0c3", fontSize: 12 }}>Estimated Costs</div>
                <div style={{ color: "#fbbf24", fontSize: 18, fontWeight: 900 }}>
                  {money(estimatedJobCosts)}
                </div>
              </div>
              <div>
                <div style={{ color: "#9fb0c3", fontSize: 12 }}>Actual Costs</div>
                <div style={{ color: "#86efac", fontSize: 18, fontWeight: 900 }}>
                  {money(actualJobCosts)}
                </div>
              </div>
              <div>
                <div style={{ color: "#9fb0c3", fontSize: 12 }}>Variance</div>
                <div style={{ color: "#ffffff", fontSize: 18, fontWeight: 900 }}>
                  {money(costVariance)}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 14,
              padding: 14,
              borderRadius: 14,
              border: "1px solid rgba(255,255,255,0.10)",
              background: "rgba(255,255,255,0.03)",
            }}
          >
            <div
              style={{
                color: "#f5c542",
                fontSize: 12,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 10,
              }}
            >
              Cost Breakdown
            </div>

            {sortedCategoryTotals.length === 0 ? (
              <div style={{ color: "#9fb0c3" }}>
                No categorized costs yet.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
                  gap: 10,
                }}
              >
                {sortedCategoryTotals.map(([category, total]) => (
                  <div
                    key={category}
                    style={{
                      padding: 10,
                      borderRadius: 10,
                      background: "#0f2235",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <div
                      style={{
                        color: "#9fb0c3",
                        fontSize: 12,
                        marginBottom: 4,
                      }}
                    >
                      {category}
                    </div>

                    <div
                      style={{
                        color: "#ffffff",
                        fontSize: 18,
                        fontWeight: 800,
                      }}
                    >
                      {money(total)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {jobCosts.length === 0 ? (
            <p style={{ color: "#9fb0c3" }}>No job costs tracked yet.</p>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <select
                  value={costCategoryFilter}
                  onChange={(e) => setCostCategoryFilter(e.target.value)}
                  style={{ ...input, maxWidth: 220 }}
                  {...focusHandlers}
                >
                  <option value="all">All Categories</option>
                  {costCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>

                <select
                  value={costVendorFilter}
                  onChange={(e) => setCostVendorFilter(e.target.value)}
                  style={{ ...input, maxWidth: 220 }}
                  {...focusHandlers}
                >
                  <option value="all">All Vendors</option>
                  {costVendors.map((vendor) => (
                    <option key={vendor} value={vendor}>
                      {vendor}
                    </option>
                  ))}
                </select>

                <div style={{ color: "#9fb0c3", fontSize: 13 }}>
                  Filtered total: <strong style={{ color: "#ffffff" }}>{money(filteredJobCostsTotal)}</strong>
                </div>
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={tableTh}>Description</th>
                  <th style={tableTh}>Vendor</th>
                  <th style={tableTh}>Category</th>
                  <th style={tableTh}>Qty</th>
                  <th style={tableTh}>Unit Cost</th>
                  <th style={tableTh}>Total</th>
                  <th style={tableTh}>Status</th>
                  <th style={tableTh}>Receipt</th>
                  <th style={tableTh}>Bill</th>
                  <th style={tableTh}></th>
                </tr>
              </thead>
              <tbody>
                {filteredJobCosts.map((c) => (
                  <tr key={c.id}>
                    <td style={tableTd}>{c.description || "—"}</td>
                    <td style={tableTd}>{c.vendor_name || "—"}</td>
                    <td style={tableTd}>{c.cost_category || "—"}</td>
                    <td style={tableTd}>{Number(c.quantity || 0).toFixed(2)}</td>
                    <td style={tableTd}>{money(c.unit_cost)}</td>
                    <td style={tableTd}>{money(c.total_cost)}</td>
                    <td style={tableTd}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 9px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 900,
                          textTransform: "uppercase",
                          background:
                            c.cost_status === "estimated"
                              ? "rgba(245,158,11,0.16)"
                              : "rgba(34,197,94,0.16)",
                          color:
                            c.cost_status === "estimated" ? "#fbbf24" : "#86efac",
                        }}
                      >
                        {c.cost_status || "actual"}
                      </span>
                    </td>
                    <td style={tableTd}>
                      {c.receipt_file_url ? (
                        <a href={c.receipt_file_url} target="_blank" rel="noreferrer" style={linkStyle}>
                          View Receipt
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={tableTd}>
                      {c.bill_file_url ? (
                        <a href={c.bill_file_url} target="_blank" rel="noreferrer" style={linkStyle}>
                          View Bill
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={tableTd}>
                      <button
                        onClick={() => deleteJobCost(c.id)}
                        style={{
                          ...buttonSecondary,
                          padding: "6px 10px",
                          fontSize: 12,
                        }}
                        {...secondaryHoverHandlers}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
